import { race } from '@redux-saga/core/effects';
import { take } from 'redux-saga-test-plan/matchers';
import { all, call, getContext, put, select, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import type { Action } from '../utils';
import i18n from '../../../i18n';
import type { IMetadata, IPortalAccountEmailAddress, IPortalAccountPhoneNumber, IPortalAccount } from '../../api/domain';
import type { CustomerBaseApiService, PortalAccountApiService } from '../../api/services';
import { NotificationType } from '../../enum';
import { addNotification, hideProgressIndicator, showProgressIndicator } from '../application/application-actions';
import { logError } from '../logging-api/logging-api-actions';
import {
    createAccountEmail,
    createAccountEmailFailure,
    createAccountEmailSuccess,
    createAccountPhoneNumber,
    createAccountPhoneNumberFailure,
    createAccountPhoneNumberSuccess,
    deleteAccountEmail,
    deleteAccountEmailFailure,
    deleteAccountEmailSuccess,
    deleteAccountPhoneNumber,
    deleteAccountPhoneNumberFailure,
    deleteAccountPhoneNumberSuccess,
    fetchPortalAccounts,
    fetchPortalAccountsFailure,
    fetchPortalAccountsSuccess,
    fetchUserPortalAccount,
    fetchUserPortalAccountFailure,
    fetchUserPortalAccountSuccess,
    setMailNotificationsEnabled,
    setMailNotificationsEnabledFailure,
    setMailNotificationsEnabledSuccess,
    setPhoneNotificationsEnabled,
    setPhoneNotificationsEnabledFailure,
    setPhoneNotificationsEnabledSuccess,
    updateAccountEmail,
    updateAccountEmailFailure,
    updateAccountEmailSuccess,
    updateAccountMetadata,
    updateAccountMetadataFailure,
    updateAccountMetadataSuccess,
    updateAccountPhoneNumber,
    updateAccountPhoneNumberFailure,
    updateAccountPhoneNumberSuccess,
    updateOrAddSingleAccountMetadata,
    updateOrAddSingleAccountMetadataFailure,
    updateOrAddSingleAccountMetadataSuccess,
} from './portal-account-api-actions';
import { getMetadata, getPortalAccounts, getUserPortalAccountMailAddresses, getUserPortalAccountPhoneNumbers } from './portal-account-api-selectors';
import de from './translations/de.json';
import en from './translations/en.json';
import fr from './translations/fr.json';
import it from './translations/it.json';

function* addTranslations() {
    i18n.addResources('de', de);
    i18n.addResources('en', en);
    i18n.addResources('fr', fr);
    i18n.addResources('it', it);

    yield;
}

function* handleFetchUserPortalAccount() {
    const api: PortalAccountApiService = yield getContext('portalAccountApiService');
    try {
        const portalAccounts: IPortalAccount[] = yield call([api, api.fetchUserPortalAccount]);
        yield put(fetchUserPortalAccountSuccess(portalAccounts[0]));
    } catch (e: unknown) {
        yield put(fetchUserPortalAccountFailure());
    }
}

function* handleFetchPortalAccountFailure() {
    yield put(
        addNotification({
            title: i18n.t('portal-account-api.error.fetch-portal-account'),
            type: NotificationType.ERROR,
        }),
    );
}

function* handleFetchPortalAccounts(action: Action<string[]>) {
    const api: PortalAccountApiService = yield getContext('portalAccountApiService');
    try {
        const requestedPortalAccountLoginIds = action.payload;
        const storedPortalAccounts: IPortalAccount[] = yield select(getPortalAccounts);
        const storedPortalAccountIds = storedPortalAccounts.map((portalAccount) => portalAccount.loginId);
        const portalAccountLoginIdsToFetch = [...new Set(requestedPortalAccountLoginIds.filter((loginId) => !storedPortalAccountIds.includes(loginId)))];
        const portalAccountsList: IPortalAccount[][] = yield all(
            portalAccountLoginIdsToFetch.map((portalAccountId: string) => call([api, api.fetchPortalAccounts], portalAccountId)),
        );
        yield put(fetchPortalAccountsSuccess(storedPortalAccounts.concat(portalAccountsList.map((portalAccounts) => portalAccounts[0]))));
    } catch (error: unknown) {
        yield put(logError(`could not fetch portal accounts with ids [${action.payload.join(',')}]: ${error}`));
        yield put(fetchPortalAccountsFailure());
    }
}

function* handleUpdateOrAddSingleAccountMetadata({ payload }: Action<IMetadata>) {
    try {
        const metadata: IMetadata[] | undefined = yield select(getMetadata);
        yield put(updateAccountMetadata([...(metadata || []).filter(({ key }) => key !== payload.key), payload]));
        yield take([updateAccountMetadataSuccess, updateAccountMetadataFailure]);
        yield put(updateOrAddSingleAccountMetadataSuccess());
    } catch (error: unknown) {
        yield put(logError(`updateOrAddSingleAccountMetadata failed with: ${error}`));
        yield put(updateOrAddSingleAccountMetadataFailure());
    }
}

function* handleUpdateAccountMetadata({ payload: metadata }: Action<IMetadata[]>) {
    const api: CustomerBaseApiService = yield getContext('customerBaseApiService');
    try {
        yield call([api, api.setPortalAccountMetadata], metadata);
        yield put(updateAccountMetadataSuccess());
    } catch (error: unknown) {
        yield put(logError(`updateAccountMetadata failed with: ${error}`));
        yield put(updateAccountMetadataFailure());
    }
}

function* handleUpdateAccountMailAddress(action: Action<IPortalAccountEmailAddress>) {
    const api: CustomerBaseApiService = yield getContext('customerBaseApiService');
    try {
        yield call([api, api.setPortalAccountEmail], action.payload);
        yield put(updateAccountEmailSuccess());
    } catch (error: unknown) {
        yield put(logError(`updateAccountMail failed with: ${error}`));
        yield put(updateAccountEmailFailure());
    }
}

function* handleUpdateAccountMailSuccess() {
    yield put(fetchUserPortalAccount());
    yield put(
        addNotification({
            title: i18n.t('portal-account-api.success.update-mail'),
            type: NotificationType.INFO,
        }),
    );
}

function* handleUpdateAccountMailFailure() {
    yield put(
        addNotification({
            title: i18n.t('portal-account-api.error.update-mail'),
            type: NotificationType.ERROR,
        }),
    );
}

function* handleDeleteAccountMailAddress({ payload: emailId }: Action<string>) {
    const api: CustomerBaseApiService = yield getContext('customerBaseApiService');
    try {
        yield put(showProgressIndicator());
        // Ignore emailId as currently only one email address is supported
        yield call([api, api.deletePortalAccountEmail]);
        yield put(deleteAccountEmailSuccess());
    } catch (error: unknown) {
        yield put(logError(`deleteAccountMail "${emailId}" failed with: ${error}`));
        yield put(deleteAccountEmailFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleDeleteAccountMailSuccess() {
    yield put(fetchUserPortalAccount());
    yield put(
        addNotification({
            title: i18n.t('portal-account-api.success.delete-mail'),
            type: NotificationType.INFO,
        }),
    );
}

function* handleDeleteAccountMailFailure() {
    yield put(
        addNotification({
            title: i18n.t('portal-account-api.error.delete-mail'),
            type: NotificationType.ERROR,
        }),
    );
}

function* handleCreateAccountMailAddress({ payload }: Action<IPortalAccountEmailAddress>) {
    const api: CustomerBaseApiService = yield getContext('customerBaseApiService');
    try {
        yield put(showProgressIndicator());
        yield call([api, api.setPortalAccountEmail], payload);
        yield put(createAccountEmailSuccess());
    } catch (error: unknown) {
        yield put(logError(`createAccountMailAddress failed with: ${error}`));
        yield put(createAccountEmailFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleCreateAccountMailSuccess() {
    yield put(
        addNotification({
            title: i18n.t('portal-account-api.success.create-mail'),
            type: NotificationType.INFO,
        }),
    );
}

function* handleCreateAccountMailFailure() {
    yield put(
        addNotification({
            title: i18n.t('portal-account-api.error.create-mail'),
            type: NotificationType.ERROR,
        }),
    );
}

function* handleUpdateAccountPhoneNumber(action: Action<IPortalAccountPhoneNumber>) {
    const api: CustomerBaseApiService = yield getContext('customerBaseApiService');
    try {
        yield put(showProgressIndicator());
        yield call([api, api.setPortalAccountPhoneNumber], action.payload);
        yield put(updateAccountPhoneNumberSuccess());
    } catch (error: unknown) {
        yield put(logError(`updateAccountPhoneNumber failed with: ${error}`));
        yield put(updateAccountPhoneNumberFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleUpdateAccountPhoneSuccess() {
    yield put(fetchUserPortalAccount());
    yield put(
        addNotification({
            title: i18n.t('portal-account-api.success.update-phone'),
            type: NotificationType.INFO,
        }),
    );
}

function* handleUpdateAccountPhoneFailure() {
    yield put(
        addNotification({
            title: i18n.t('portal-account-api.error.update-phone'),
            type: NotificationType.ERROR,
        }),
    );
}

function* handleDeleteAccountPhoneNumber({ payload: phoneNumberId }: Action<string>) {
    const api: CustomerBaseApiService = yield getContext('customerBaseApiService');
    try {
        yield put(showProgressIndicator());
        yield call([api, api.deletePortalAccountPhoneNumber]);
        yield put(deleteAccountPhoneNumberSuccess());
    } catch (error: unknown) {
        yield put(logError(`deleteAccountPhoneNumber "${phoneNumberId}" failed with: ${error}`));
        yield put(deleteAccountPhoneNumberFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleDeleteAccountPhoneSuccess() {
    yield put(fetchUserPortalAccount());
    yield put(
        addNotification({
            title: i18n.t('portal-account-api.success.delete-phone'),
            type: NotificationType.INFO,
        }),
    );
}

function* handleDeleteAccountPhoneFailure() {
    yield put(
        addNotification({
            title: i18n.t('portal-account-api.error.delete-phone'),
            type: NotificationType.ERROR,
        }),
    );
}

function* handleCreateAccountPhoneNumber({ payload }: Action<IPortalAccountPhoneNumber>) {
    const api: CustomerBaseApiService = yield getContext('customerBaseApiService');
    try {
        yield put(showProgressIndicator());
        yield call([api, api.setPortalAccountPhoneNumber], payload);
        yield put(createAccountPhoneNumberSuccess());
    } catch (error: unknown) {
        yield put(logError(`createAccountPhoneNumber failed with: ${error}`));
        yield put(createAccountPhoneNumberFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleCreateAccountPhoneSuccess() {
    yield put(
        addNotification({
            title: i18n.t('portal-account-api.success.create-phone'),
            type: NotificationType.INFO,
        }),
    );
}

function* handleCreateAccountPhoneFailure() {
    yield put(
        addNotification({
            title: i18n.t('portal-account-api.error.create-phone'),
            type: NotificationType.ERROR,
        }),
    );
}

function* handleSetMailNotificationsEnabled(action: Action<boolean>) {
    try {
        yield put(showProgressIndicator());
        const emailAddresses: IPortalAccountEmailAddress[] = yield select(getUserPortalAccountMailAddresses);
        const [firstEmailAddressesNotificationsEnabled] = emailAddresses.map((mail) => ({
            ...mail,
            forNotification: action.payload,
        }));
        yield put(updateAccountEmail(firstEmailAddressesNotificationsEnabled));
        const { failure } = yield race({
            success: take(updateAccountEmailSuccess),
            failure: take(updateAccountEmailFailure),
        });
        if (failure) {
            throw new Error('Could not set email notifications');
        }
        yield put(setMailNotificationsEnabledSuccess());
    } catch (error: unknown) {
        yield put(logError(`could not toggle email notification: ${error}`));
        yield put(setMailNotificationsEnabledFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleSetPhoneNotificationsEnabled(action: Action<boolean>) {
    try {
        const phoneNumbers: IPortalAccountPhoneNumber[] = yield select(getUserPortalAccountPhoneNumbers);
        const [firstPhoneNumberNotificationsEnabled] = phoneNumbers.map((phoneNumber) => ({
            ...phoneNumber,
            forNotification: action.payload,
        }));
        yield put(updateAccountPhoneNumber(firstPhoneNumberNotificationsEnabled));
        const { failure } = yield race({
            success: take(updateAccountPhoneNumberSuccess),
            failure: take(updateAccountPhoneNumberFailure),
        });
        if (failure) {
            throw new Error('Could not set phone notifications');
        }
        yield put(setPhoneNotificationsEnabledSuccess());
    } catch (error: unknown) {
        yield put(setPhoneNotificationsEnabledFailure());
    }
}

export function* portalAccountApiSaga() {
    yield all([
        call(addTranslations),
        takeEvery(fetchUserPortalAccount, handleFetchUserPortalAccount),
        takeEvery(fetchPortalAccounts, handleFetchPortalAccounts),
        takeEvery(fetchUserPortalAccountFailure, handleFetchPortalAccountFailure),
        takeLatest(updateOrAddSingleAccountMetadata, handleUpdateOrAddSingleAccountMetadata),

        takeEvery(updateAccountMetadata, handleUpdateAccountMetadata),
        takeEvery(updateAccountMetadataSuccess, handleFetchUserPortalAccount),

        takeEvery(updateAccountEmail, handleUpdateAccountMailAddress),
        takeEvery(createAccountEmail, handleCreateAccountMailAddress),
        takeEvery(deleteAccountEmail, handleDeleteAccountMailAddress),

        takeEvery(updateAccountEmailSuccess, handleUpdateAccountMailSuccess),
        takeEvery(createAccountEmailSuccess, handleCreateAccountMailSuccess),
        takeEvery(deleteAccountEmailSuccess, handleDeleteAccountMailSuccess),

        takeEvery(updateAccountEmailFailure, handleUpdateAccountMailFailure),
        takeEvery(createAccountEmailFailure, handleCreateAccountMailFailure),
        takeEvery(deleteAccountEmailFailure, handleDeleteAccountMailFailure),

        takeEvery(updateAccountPhoneNumber, handleUpdateAccountPhoneNumber),
        takeEvery(createAccountPhoneNumber, handleCreateAccountPhoneNumber),
        takeEvery(deleteAccountPhoneNumber, handleDeleteAccountPhoneNumber),

        takeEvery(updateAccountPhoneNumberSuccess, handleUpdateAccountPhoneSuccess),
        takeEvery(createAccountPhoneNumberSuccess, handleCreateAccountPhoneSuccess),
        takeEvery(deleteAccountPhoneNumberSuccess, handleDeleteAccountPhoneSuccess),

        takeEvery(updateAccountPhoneNumberFailure, handleUpdateAccountPhoneFailure),
        takeEvery(createAccountPhoneNumberFailure, handleCreateAccountPhoneFailure),
        takeEvery(deleteAccountPhoneNumberFailure, handleDeleteAccountPhoneFailure),

        takeLeading(setMailNotificationsEnabled, handleSetMailNotificationsEnabled),
        takeLeading(setPhoneNotificationsEnabled, handleSetPhoneNotificationsEnabled),
    ]);
}
